import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Panels } from '@/components/layouts';
import {
  AREAS,
  ProjectAreaAside,
  ProjectHeaderBar,
  useProjectHeaderData,
} from '@/components/project';
import type { ProjectArea } from '@/components/project';
import { EmptyState } from '@/components/ui';

/**
 * Team and Billing: real routes, real header, real page shape, nothing behind
 * them yet.
 *
 * They exist because the header now shows all four of a project's areas, and a
 * pill that navigates nowhere is worse than no pill — `App.tsx`'s catch-all
 * would bounce the click to the portfolio, which reads as a logout. Mounted in
 * BOTH id spaces for the same reason.
 *
 * It runs through `Panels` with the drawer landing collapsed, exactly as the
 * Financials empty state does: the rail is present, so the page is not the one
 * project surface missing a right-hand side, but nothing opens that has
 * nothing to show. A collapsed drawer also hands main the whole remainder, so
 * there is no grey third beside the empty state.
 */
export type ProjectAreaPlaceholderPageProps = {
  area: Extract<ProjectArea, 'team' | 'billing'>;
};

export const ProjectAreaPlaceholderPage = ({ area }: ProjectAreaPlaceholderPageProps) => {
  const { t } = useTranslation(['pmShell', 'projectManagement']);
  const header = useProjectHeaderData();
  const option = AREAS.find((candidate) => candidate.area === area)!;

  return (
    <Box
      sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}
      data-automation-id="project-area-page"
      data-area={area}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Panels
          // One key per area, never one shared key: `autoSaveId` stores the
          // whole group layout, so Team and Billing sharing it would let a
          // width dragged on one become the other's remembered one.
          autoSaveId={`pm-${area}-panels`}
          defaultCollapsed
          collapseDrawerOnEscape
        >
          <Panels.Main>
            <ProjectHeaderBar projectName={header.projectName} projectCode={header.projectCode} />
            {/*
              The section header and its card are ONE child of PanelContent's
              `Stack gap={2}`, as PlanPanel and the Financials empty state are.
              As siblings they would collect that 16px on top of the header
              row's own `mb: 2` and sit lower than every other page's.
            */}
            <Box>
              <Typography variant="h4" component="h2" sx={{ mb: 2 }}>
                {t(option.labelKey)}
              </Typography>
              <Box
                sx={{
                  bgcolor: 'var(--bg-panel)',
                  borderRadius: 'var(--border-radius-medium)',
                  border: 'var(--border-width-hairline) solid var(--border-subtle)',
                  overflow: 'hidden',
                }}
              >
                <EmptyState
                  automationId={`${area}-empty`}
                  icon={option.icon}
                  title={t(`areaPlaceholder.${area}.Title`)}
                  body={t(`areaPlaceholder.${area}.Body`)}
                />
              </Box>
            </Box>
          </Panels.Main>
          <Panels.Drawer label={t(`areaPlaceholder.${area}.DrawerLabel`)}>
            <ProjectAreaAside
              title={t(`areaPlaceholder.${area}.AsideTitle`)}
              projectName={header.projectName}
              projectCode={header.projectCode}
              customerName={header.customerName}
              note={t(`areaPlaceholder.${area}.Aside`)}
            />
          </Panels.Drawer>
        </Panels>
      </Box>
    </Box>
  );
};
